/**
 * The ghost: an outlined, nearly clear capsule (the game's line look) that follows a cached Trajectory. It is
 * pure presentation. It has no collider, is never seen by the simulation, its
 * triggers or its timer, and it never simulates anything per frame: each frame
 * it reads one interpolated position from the precomputed trajectory.
 *
 * Synchronisation (GhostClock): ghost run time = live run time + offset,
 * where live run time is measured on simulation time from the live attempt's
 * timer start, at the rendered interpolation point. So the ghost pauses when
 * the game pauses (no ticks), rewinds to its start on restart (new attempt),
 * waits at the start line until the live timer starts, and holds where it was
 * when the live run finishes. A ghost that has finished holds at its finish.
 */
import * as THREE from 'three';
import { positionAt, sampleAt, type Trajectory } from './Trajectory';

/** The body's fill, as a fraction of the ghost's opacity: a faint haze inside the lines. */
const FILL = 0.3;

export class GhostView {
  readonly group = new THREE.Group();
  private readonly material: THREE.MeshBasicMaterial;
  private readonly edgeMaterial: THREE.LineBasicMaterial;
  private readonly geometry: THREE.CapsuleGeometry;
  private readonly edges: THREE.EdgesGeometry;
  private trajectory: Trajectory | null = null;
  private readonly p = { x: 0, y: 0, z: 0 };
  private readonly halfHeight: number;

  constructor(radius: number, height: number, color = 0x8fd3ff) {
    this.halfHeight = height / 2;
    this.geometry = new THREE.CapsuleGeometry(radius, Math.max(0.01, height - 2 * radius), 4, 12);
    this.material = new THREE.MeshBasicMaterial({ color, transparent: true, opacity: 0.35 * FILL, depthWrite: false });
    this.edges = new THREE.EdgesGeometry(this.geometry, 20);
    this.edgeMaterial = new THREE.LineBasicMaterial({ color, transparent: true, opacity: 0.6, depthWrite: false });
    const body = new THREE.Mesh(this.geometry, this.material);
    body.renderOrder = 10;
    const outline = new THREE.LineSegments(this.edges, this.edgeMaterial);
    outline.renderOrder = 11;
    this.group.add(body, outline);
    this.group.name = 'ghost';
    this.group.visible = false;
  }

  setTrajectory(t: Trajectory | null): void {
    this.trajectory = t;
    if (!t) this.group.visible = false;
  }

  /** 0..1; the outline is kept a little stronger so a faint ghost stays readable. */
  setOpacity(opacity: number): void {
    const a = Math.min(1, Math.max(0.05, opacity));
    this.material.opacity = a * FILL;
    this.edgeMaterial.opacity = Math.min(1, a + 0.35);
  }

  /** Places the ghost at ghost run time `seconds`; null hides it. */
  update(seconds: number | null): void {
    const t = this.trajectory;
    if (!t || seconds === null) {
      this.group.visible = false;
      return;
    }
    positionAt(t, sampleAt(t, seconds), this.p);
    this.group.position.set(this.p.x, this.p.y + this.halfHeight, this.p.z);
    this.group.visible = true;
  }

  dispose(): void {
    this.group.removeFromParent();
    this.geometry.dispose();
    this.edges.dispose();
    this.material.dispose();
    this.edgeMaterial.dispose();
  }
}

/**
 * Live run time for the ghost, on simulation time. Feed it the live
 * simulation's attempt events; ask it for the time at the rendered point.
 */
export class GhostClock {
  /** Absolute simulation time (s) the live timer started at; null while waiting at the start. */
  private startTime: number | null = null;
  /** Live finish run time once finished (the ghost holds there). */
  private heldAt: number | null = null;
  offset = 0;

  attemptStarted(simTime: number): void {
    this.startTime = simTime;
    this.heldAt = null;
  }

  attemptReset(): void {
    this.startTime = null;
    this.heldAt = null;
  }

  finished(seconds: number): void {
    this.heldAt = seconds;
  }

  /**
   * Ghost run time at the rendered point: `lastTick` is the index of the most
   * recent simulated tick and `alpha` the render interpolation within it.
   * Before the live start the ghost waits at run time `offset` (its start, or
   * the chosen viewing offset).
   */
  time(lastTick: number, alpha: number, dt: number): number {
    const base = this.heldAt ?? (this.startTime === null ? 0 : Math.max(0, (lastTick + alpha) * dt - this.startTime));
    return Math.max(0, base + this.offset);
  }
}
